import {
  format,
  addDays,
  subDays,
  startOfDay,
  endOfDay,
  isSameDay,
} from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, Calendar, Clock, MapPin, User } from "lucide-react";
import { useLocation } from "wouter";

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  primaryColor?: string;
}

interface CalendarEvent {
  id: string;
  title: string;
  date?: Date;
  scheduledStartDate?: Date;
  scheduledEndDate?: Date;
  staffId?: string;
  assignedTo?: string;
  assignedStaff?: { id: string; firstName?: string; lastName?: string }[];
  status?: string;
  customer?: string;
  address?: string;
  duration?: number; // Duration in hours
  [key: string]: any;
}

export interface DayViewProps {
  date: Date;
  staff?: StaffMember[];
  events?: CalendarEvent[];
  holidays?: PublicHoliday[];
  onChangeDate?: (newDate: Date) => void;
  onOpenJob?: (jobId: string) => void;
  onEventMove?: (update: {
    eventId: string;
    newStart: Date;
    newEnd: Date;
    staffId?: string;
  }) => void;
}

import type { JobStatus } from "@/shared/types";
import { normalizeLegacyStatus } from "@/shared/types";
import type { PublicHoliday } from "@/hooks/usePublicHolidays";
import { getHolidayForDate } from "@/hooks/usePublicHolidays";

const statusConfig: Record<JobStatus, { bg: string; text: string; border: string }> = {
  "quote-needed": { 
    bg: "bg-red-50 dark:bg-red-950/40", 
    text: "text-red-700 dark:text-red-300",
    border: "border-l-red-500"
  },
  "quoted": { 
    bg: "bg-orange-50 dark:bg-orange-950/40", 
    text: "text-orange-700 dark:text-orange-300",
    border: "border-l-orange-500"
  },
  "accepted": { 
    bg: "bg-green-50 dark:bg-green-950/40", 
    text: "text-green-700 dark:text-green-300",
    border: "border-l-green-500"
  },
  "in-progress": { 
    bg: "bg-blue-50 dark:bg-blue-950/40", 
    text: "text-blue-700 dark:text-blue-300",
    border: "border-l-blue-500"
  },
  "complete": { 
    bg: "bg-indigo-50 dark:bg-indigo-950/40", 
    text: "text-indigo-700 dark:text-indigo-300",
    border: "border-l-indigo-600"
  },
  "invoiced": { 
    bg: "bg-purple-50 dark:bg-purple-950/40", 
    text: "text-purple-700 dark:text-purple-300",
    border: "border-l-purple-500"
  },
  "paid": { 
    bg: "bg-pink-50 dark:bg-pink-950/40", 
    text: "text-pink-700 dark:text-pink-300",
    border: "border-l-pink-500"
  },
  "archived": { 
    bg: "bg-slate-100 dark:bg-slate-800/60", 
    text: "text-slate-600 dark:text-slate-400",
    border: "border-l-slate-400"
  },
};

export function DayView({
  date,
  staff = [],
  events = [],
  holidays = [],
  onChangeDate,
  onOpenJob,
  onEventMove,
}: DayViewProps) {
  const [, navigate] = useLocation();
  const start = startOfDay(date);
  const end = endOfDay(date);
  const holiday = getHolidayForDate(holidays, date);

  const handleDateChange = (newDate: Date) => {
    if (onChangeDate) {
      onChangeDate(newDate);
    }
  };

  const handleOpenJob = (jobId: string) => {
    if (onOpenJob) {
      onOpenJob(jobId);
    } else {
      navigate(`/job/${jobId}`);
    }
  };

  const filteredEvents = events
    .filter((evt) => {
      const evtStart = evt.scheduledStartDate || evt.date || new Date();
      const evtEnd = evt.scheduledEndDate || evtStart;
      return evtStart <= end && evtEnd >= start;
    })
    .sort((a, b) => {
      const aStart = a.scheduledStartDate || a.date || new Date();
      const bStart = b.scheduledStartDate || b.date || new Date();
      return new Date(aStart).getTime() - new Date(bStart).getTime();
    });

  const isToday = isSameDay(date, new Date());

  return (
    <div className="w-full h-full flex flex-col bg-background rounded-xl overflow-hidden shadow-sm border border-border/40">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 py-4 sm:px-6 sm:py-5 border-b border-border bg-white dark:bg-gray-800/50 gap-4">
        <div className="flex items-start sm:items-center gap-3">
          <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-lg flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDateChange(subDays(date, 1))}
              data-testid="button-prev-day"
              className="h-8 w-8 rounded-md hover:bg-background shadow-none"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <Button
              variant={isToday ? "secondary" : "ghost"}
              size="sm"
              onClick={() => handleDateChange(new Date())}
              data-testid="button-today"
              className={`h-8 px-3 text-xs font-semibold rounded-md ${isToday ? 'bg-white dark:bg-gray-700 shadow-sm' : ''}`}
            >
              Today
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDateChange(addDays(date, 1))}
              data-testid="button-next-day"
              className="h-8 w-8 rounded-md hover:bg-background shadow-none"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex flex-col min-w-0">
            <h2 className="text-sm sm:text-lg font-bold tracking-tight text-foreground" data-testid="text-current-date">
              {format(date, "EEEE, MMMM d")}
            </h2>
            {holiday && (
              <span
                className="text-xs font-medium px-2 py-0.5 rounded-full w-fit mt-1"
                style={{ background: "#FEE2E2", color: "#991B1B" }}
                title={holiday.name}
              >
                🏖️ {holiday.localName || holiday.name}
              </span>
            )}
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-2 text-muted-foreground bg-muted/30 px-3 py-1.5 rounded-full">
          <Clock className="h-3.5 w-3.5" />
          <span className="text-xs font-medium uppercase tracking-wider">{filteredEvents.length} Jobs</span>
        </div>
      </div>

      {/* Event List */}
      <div className="flex-1 p-4 sm:p-6 overflow-auto bg-gray-50/50 dark:bg-gray-900/50">
        {filteredEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-12 bg-white dark:bg-gray-800 rounded-xl border-2 border-dashed border-muted">
            <div className="w-16 h-16 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-4">
              <Calendar className="h-8 w-8 text-blue-500 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">No jobs today</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-[200px]">
              You don't have any jobs scheduled for this date.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredEvents.map((evt) => {
              const normalizedStatus = normalizeLegacyStatus(evt.status || "quote-needed") as JobStatus;
              const colors = statusConfig[normalizedStatus] || statusConfig["quote-needed"];
              
              return (
                <Card 
                  key={evt.id} 
                  className={`group relative overflow-hidden transition-all hover:shadow-md hover:border-primary/20 border-l-4 ${colors.border} bg-white dark:bg-gray-800`}
                  onClick={() => handleOpenJob(evt.id)}
                  data-testid={`event-card-${evt.id}`}
                >
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex justify-between items-start mb-3">
                      <div className="space-y-1 min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                            {evt.jobNumber || "JOB-NEW"}
                          </span>
                          <Badge 
                            variant="secondary" 
                            className={`text-[10px] px-1.5 py-0 font-bold uppercase ${colors.bg} ${colors.text} border-none`}
                          >
                            {normalizedStatus.replace('-', ' ')}
                          </Badge>
                        </div>
                        <h3 className="font-bold text-base sm:text-lg leading-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
                          {evt.title}
                        </h3>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-y-2.5">
                      <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                        <div className="flex items-center justify-center w-5 h-5 rounded bg-muted/50">
                          <Clock className="h-3.5 w-3.5" />
                        </div>
                        <span className="font-medium">
                          {evt.scheduledStartDate ? format(new Date(evt.scheduledStartDate), "h:mm a") : "TBD"}
                          {evt.scheduledEndDate && ` - ${format(new Date(evt.scheduledEndDate), "h:mm a")}`}
                        </span>
                      </div>

                      {evt.customer && (
                        <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                          <div className="flex items-center justify-center w-5 h-5 rounded bg-muted/50">
                            <User className="h-3.5 w-3.5" />
                          </div>
                          <span className="truncate">{evt.customer}</span>
                        </div>
                      )}

                      {evt.address && (
                        <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                          <div className="flex items-center justify-center w-5 h-5 rounded bg-muted/50">
                            <MapPin className="h-3.5 w-3.5" />
                          </div>
                          <span className="truncate" title={evt.address}>{evt.address}</span>
                        </div>
                      )}
                    </div>

                    {evt.assignedStaff && evt.assignedStaff.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-border flex flex-wrap gap-1.5">
                        {evt.assignedStaff.map((s) => (
                          <Badge 
                            key={s.id} 
                            variant="outline" 
                            className="bg-muted/30 text-[10px] font-medium border-none px-2 py-0.5"
                          >
                            {s.firstName} {s.lastName}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
